import { useMemo, useState } from "react"
import {
  Box,
  Button,
  Drawer,
  HStack,
  IconButton,
  Input,
  Portal,
  SimpleGrid,
  Stack,
  Table,
} from "@chakra-ui/react"
import { FaChevronDown, FaChevronRight, FaTimes } from "react-icons/fa"
import { Text } from "@/components"
import { IrrDeal, IrrDealFund } from "@/types"

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtCurrency = (v: string | number | null | undefined) => {
  if (v == null || v === "") return "—"
  const n = typeof v === "string" ? parseFloat(v) : v
  if (isNaN(n)) return "—"
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}
const fmtPct = (v: string | number | null | undefined) => {
  if (v == null) return "—"
  const n = typeof v === "string" ? parseFloat(v) : v
  if (isNaN(n)) return "—"
  return `${(n * 100).toFixed(1)}%`
}
const fmtX = (v: string | number | null | undefined) => {
  if (v == null) return "—"
  const n = typeof v === "string" ? parseFloat(v) : v
  if (isNaN(n)) return "—"
  return `${n.toFixed(2)}×`
}
const fmtDate = (v: string | undefined) => {
  if (!v) return "—"
  return new Date(v).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

// ── Company grouping ──────────────────────────────────────────────────────────
interface CompanyGroup {
  companyId: number
  companyName: string
  companyUrl: string
  deals: IrrDeal[]
  totalShares: number
  totalResidual: number    // LRP-based (display only)
  totalHoldingValue: number // FMV-based (used for TVPI)
  totalRealized: number
  totalInvested: number
  isExited: boolean
}

function groupDealsByCompany(deals: IrrDeal[]): CompanyGroup[] {
  const map = new Map<number, CompanyGroup>()
  for (const d of deals) {
    const existing = map.get(d.companyId) ?? {
      companyId: d.companyId,
      companyName: d.companyName,
      companyUrl: `/core/company/${d.companyId}`,
      deals: [],
      totalShares: 0,
      totalResidual: 0,
      totalHoldingValue: 0,
      totalRealized: 0,
      totalInvested: 0,
      isExited: false,
    }
    existing.deals.push(d)
    existing.totalShares += d.numSharesCurrent
    existing.totalResidual += parseFloat(d.residualValue) || 0
    existing.totalHoldingValue += d.holdingValue ? (parseFloat(d.holdingValue) || 0) : 0
    existing.totalRealized += parseFloat(d.totalRealizations) || 0
    existing.totalInvested += parseFloat(d.esoTotalPayment) || 0
    map.set(d.companyId, existing)
  }
  const groups = Array.from(map.values())
  for (const g of groups) g.isExited = g.totalResidual === 0
  return groups.sort((a, b) => a.companyName.localeCompare(b.companyName))
}

// ── Deal detail panel ─────────────────────────────────────────────────────────
const ScenarioBox = ({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) => (
  <Box
    flex={1}
    px={4}
    py={3}
    border="1px solid"
    borderColor="border.default"
    borderRadius="md"
  >
    <Text fontSize="10px" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.08em" mb={1}>
      {label}
    </Text>
    <Text fontSize="xl" fontWeight="semibold" lineHeight="1.2">
      {value}
    </Text>
    {sub && <Text fontSize="xs" color="text.muted" mt={0.5}>{sub}</Text>}
  </Box>
)

const DealDetailPanel = ({
  deal,
  onClose,
}: {
  deal: IrrDeal
  onClose: () => void
}) => (
  <Drawer.Root open onOpenChange={(e: { open: boolean }) => !e.open && onClose()} placement="end" size="lg">
    <Portal>
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content>
          <Drawer.Body p={6}>
            <Stack gap={5}>
              {/* Header */}
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Stack gap={0}>
                  <Text fontSize="lg" fontWeight="bold">{deal.companyName}</Text>
                  <Text fontSize="sm" color="text.muted">
                    {deal.lastName} · Deal {deal.dealId} · {deal.dealManager}
                  </Text>
                </Stack>
                <IconButton aria-label="Close" size="sm" variant="ghost" onClick={onClose}>
                  <FaTimes />
                </IconButton>
              </HStack>

              {/* Three scenario values */}
              <Stack gap={2}>
                <Text fontSize="xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.08em">
                  Exit Scenarios
                </Text>
                <HStack gap={3} alignItems="stretch">
                  <ScenarioBox
                    label="@LRP"
                    value={fmtCurrency(deal.residualValue)}
                    sub={`LRP ${fmtCurrency(deal.lrpPrice)}`}
                  />
                  <ScenarioBox
                    label="@2×LRP"
                    value={deal.residualAt2xLrp ? fmtCurrency(deal.residualAt2xLrp) : "—"}
                  />
                  <ScenarioBox
                    label="FMV Holding"
                    value={deal.holdingValue ? fmtCurrency(deal.holdingValue) : "—"}
                    sub={`FMV ${fmtCurrency(deal.fmvCurrent)}`}
                  />
                </HStack>
              </Stack>

              {/* Key deal metrics */}
              <SimpleGrid columns={3} gap={3}>
                {[
                  { label: "PP", value: fmtPct(deal.actualDealPp) },
                  { label: "IRR", value: deal.irr ? fmtPct(deal.irr) : "—" },
                  { label: "TVPI", value: deal.tvpi ? fmtX(deal.tvpi) : "—" },
                  { label: "LP Payment", value: fmtCurrency(deal.lpPayment) },
                  { label: "ESO Payment", value: fmtCurrency(deal.esoTotalPayment) },
                  { label: "Realized", value: fmtCurrency(deal.totalRealizations) },
                  { label: "Shares", value: deal.numSharesCurrent.toLocaleString() },
                  { label: "Closing", value: fmtDate(deal.originalClosingDate) },
                  { label: "Status", value: deal.fullyExitedPortfolio === "Yes" ? "Exited" : "Active" },
                ].map(({ label, value }) => (
                  <Stack key={label} gap={0}>
                    <Text fontSize="10px" color="text.muted" textTransform="uppercase" letterSpacing="0.06em">
                      {label}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">{value}</Text>
                  </Stack>
                ))}
              </SimpleGrid>

              {/* Cash flow table */}
              {deal.cashFlows.length > 0 && (
                <Stack gap={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.08em">
                    Cash Flows
                  </Text>
                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row bg="table.row">
                          <Table.ColumnHeader>Date</Table.ColumnHeader>
                          <Table.ColumnHeader>Amount</Table.ColumnHeader>
                          <Table.ColumnHeader>Memo</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {deal.cashFlows.map((cf) => (
                          <Table.Row key={cf.id} bg="table.row">
                            <Table.Cell whiteSpace="nowrap">{fmtDate(cf.date)}</Table.Cell>
                            <Table.Cell whiteSpace="nowrap" color={cf.amount < 0 ? "red.500" : "green.500"}>
                              {fmtCurrency(cf.amount)}
                            </Table.Cell>
                            <Table.Cell>{cf.memo || "—"}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Portal>
  </Drawer.Root>
)

// ── Company row ───────────────────────────────────────────────────────────────
const CompanyRow = ({
  group,
  onSelectDeal,
}: {
  group: CompanyGroup
  onSelectDeal: (deal: IrrDeal) => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const totalValue = group.totalHoldingValue + group.totalRealized
  const tvpi = group.totalInvested > 0 ? totalValue / group.totalInvested : null

  return (
    <Stack gap={0} border="1px solid" borderColor="border.default" borderRadius="md" overflow="hidden">
      {/* Company header row */}
      <HStack
        px={4}
        py={3}
        bg="card.bg"
        cursor="pointer"
        _hover={{ bg: "hover.bg" }}
        onClick={() => setIsOpen((p) => !p)}
        gap={3}
      >
        <Box color="text.muted" w={4}>
          {isOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
        </Box>
        <Text fontWeight="semibold" flex={1} fontSize="sm">
          {group.companyName}
        </Text>
        <Text fontSize="xs" color="text.muted" minW="60px" textAlign="right">
          {group.deals.length} deal{group.deals.length !== 1 ? "s" : ""}
        </Text>
        <Text fontSize="xs" color="text.muted" minW="80px" textAlign="right">
          {group.totalShares.toLocaleString()} shares
        </Text>
        <Text fontSize="sm" minW="90px" textAlign="right">
          {fmtCurrency(group.totalResidual)}
        </Text>
        <Text fontSize="sm" minW="80px" textAlign="right" color="green.500">
          {fmtCurrency(group.totalRealized)}
        </Text>
        <Text fontSize="sm" minW="60px" textAlign="right" fontWeight="medium">
          {tvpi !== null ? fmtX(tvpi) : "—"}
        </Text>
      </HStack>

      {/* Deal sub-table */}
      {isOpen && (
        <Box overflowX="auto" borderTop="1px solid" borderColor="border.default">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="table.row">
                <Table.ColumnHeader pl={10}>Client</Table.ColumnHeader>
                <Table.ColumnHeader>Closing</Table.ColumnHeader>
                <Table.ColumnHeader>PP</Table.ColumnHeader>
                <Table.ColumnHeader>IRR</Table.ColumnHeader>
                <Table.ColumnHeader>TVPI</Table.ColumnHeader>
                <Table.ColumnHeader>Residual @LRP</Table.ColumnHeader>
                <Table.ColumnHeader>Realized</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {group.deals.map((deal) => (
                <Table.Row
                  key={deal.dealId}
                  bg="table.row"
                  cursor="pointer"
                  _hover={{ bg: "hover.bg" }}
                  onClick={() => onSelectDeal(deal)}
                >
                  <Table.Cell pl={10} whiteSpace="nowrap">{deal.lastName}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{fmtDate(deal.originalClosingDate)}</Table.Cell>
                  <Table.Cell>{fmtPct(deal.actualDealPp)}</Table.Cell>
                  <Table.Cell color={deal.irr ? (parseFloat(deal.irr) >= 0 ? "green.500" : "red.500") : undefined}>
                    {deal.irr ? fmtPct(deal.irr) : "—"}
                  </Table.Cell>
                  <Table.Cell>{deal.tvpi ? fmtX(deal.tvpi) : "—"}</Table.Cell>
                  <Table.Cell>{fmtCurrency(deal.residualValue)}</Table.Cell>
                  <Table.Cell color="green.500">{fmtCurrency(deal.totalRealizations)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Stack>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
interface Props {
  irrDealFunds: IrrDealFund[]
  isLoading: boolean
}

const ALL_FUNDS = "All Funds"

export const IrrPortfolioTab = ({ irrDealFunds, isLoading }: Props) => {
  const fundNames = useMemo(
    () => irrDealFunds.map((f) => f.whichFund).sort(),
    [irrDealFunds]
  )
  const [selectedFund, setSelectedFund] = useState<string>(fundNames[0] ?? ALL_FUNDS)
  const [search, setSearch] = useState("")
  const [showExited, setShowExited] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<IrrDeal | null>(null)

  const allDeals = useMemo(
    () => irrDealFunds.flatMap((f) => f.deals),
    [irrDealFunds]
  )

  const companyGroups = useMemo(() => {
    const deals =
      selectedFund === ALL_FUNDS
        ? allDeals
        : irrDealFunds.find((f) => f.whichFund === selectedFund)?.deals ?? []
    let groups = groupDealsByCompany(deals)
    if (!showExited) groups = groups.filter((g) => !g.isExited)
    if (!search.trim()) return groups
    const q = search.trim().toLowerCase()
    return groups.filter((g) => g.companyName.toLowerCase().includes(q))
  }, [irrDealFunds, allDeals, selectedFund, showExited, search])

  const exitedCount = useMemo(() => {
    const deals =
      selectedFund === ALL_FUNDS
        ? allDeals
        : irrDealFunds.find((f) => f.whichFund === selectedFund)?.deals ?? []
    return groupDealsByCompany(deals).filter((g) => g.isExited).length
  }, [irrDealFunds, allDeals, selectedFund])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={12}>
        <Text color="text.muted">Loading...</Text>
      </Box>
    )
  }

  return (
    <Stack gap={5}>
      {/* Fund selector + filters + search */}
      <HStack gap={3} flexWrap="wrap">
        <Button
          size="sm"
          variant={selectedFund === ALL_FUNDS ? "solid" : "outline"}
          onClick={() => setSelectedFund(ALL_FUNDS)}
        >
          All Funds
        </Button>
        {fundNames.map((name) => (
          <Button
            key={name}
            size="sm"
            variant={selectedFund === name ? "solid" : "outline"}
            onClick={() => setSelectedFund(name)}
          >
            {name}
          </Button>
        ))}
        <Button
          size="sm"
          variant={showExited ? "solid" : "outline"}
          onClick={() => setShowExited((p) => !p)}
          ml={2}
        >
          {showExited ? "Hide Exited" : `Show Exited${exitedCount > 0 ? ` (${exitedCount})` : ""}`}
        </Button>
        <Input
          size="sm"
          placeholder="Search companies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="240px"
          ml="auto"
        />
      </HStack>

      {/* Column headers */}
      <HStack
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="border.default"
        gap={3}
      >
        <Box w={4} />
        <Text fontSize="xs" color="text.muted" fontWeight="semibold" flex={1}>Company</Text>
        <Text fontSize="xs" color="text.muted" minW="60px" textAlign="right">Deals</Text>
        <Text fontSize="xs" color="text.muted" minW="80px" textAlign="right">Shares</Text>
        <Text fontSize="xs" color="text.muted" minW="90px" textAlign="right">Residual</Text>
        <Text fontSize="xs" color="text.muted" minW="80px" textAlign="right">Realized</Text>
        <Text fontSize="xs" color="text.muted" minW="60px" textAlign="right">TVPI</Text>
      </HStack>

      {/* Company rows */}
      {companyGroups.length === 0 ? (
        <Text color="text.muted" fontSize="sm">
          {search
            ? "No companies match your search."
            : !showExited && exitedCount > 0
            ? `No active companies. ${exitedCount} exited — click "Show Exited" to view.`
            : "No deals in this fund."}
        </Text>
      ) : (
        <Stack gap={2}>
          {companyGroups.map((group) => (
            <CompanyRow key={group.companyId} group={group} onSelectDeal={setSelectedDeal} />
          ))}
        </Stack>
      )}

      {/* Deal detail drawer */}
      {selectedDeal && (
        <DealDetailPanel deal={selectedDeal} onClose={() => setSelectedDeal(null)} />
      )}
    </Stack>
  )
}
